/**
 * Lot AH [29] (retours du 14/10/2026) : Réglages en menu latéral, une adresse par section (`/reglages/projets`).
 * Logique pure, testée : liste des sections et de leurs groupes, chemins, anciennes ancres `/reglages#…`.
 * Les cartes de chaque section sont rangées dans `views/Reglages.vue` (`SECTION_CARDS`).
 */

export const SETTINGS_SECTIONS = [
  { slug: 'accueil', label: 'Accueil', group: null },
  { slug: 'projets', label: 'Projets', group: 'Métier' },
  { slug: 'chiffres', label: 'Chiffres', group: 'Métier' },
  { slug: 'depenses', label: 'Dépenses', group: 'Métier' },
  { slug: 'actions', label: 'Actions et rappels', group: 'Métier' },
  // Lot BB : boîtes lues, domaines des fournisseurs, connexion Crisp.
  { slug: 'support', label: 'Support', group: 'Métier' },
  // Lot BF : coach de journée (rappels par jour, carte repoussée, bornes de la journée).
  { slug: 'coach', label: 'Coach', group: 'Métier' },
  { slug: 'membres', label: 'Membres', group: 'Équipe' },
  { slug: 'notifications', label: 'Notifications', group: 'Équipe' },
  { slug: 'elements', label: 'Éléments à rattacher', group: 'Données' },
  { slug: 'connexions', label: 'Connexions', group: 'Données' },
  { slug: 'import', label: 'Import du Google Sheet', group: 'Ponctuel' },
  { slug: 'configuration', label: 'Première configuration', group: 'Ponctuel' },
] as const;

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];
export type SettingsSlug = SettingsSection['slug'];

export const SETTINGS_HOME: SettingsSlug = 'accueil';

/** Nom de route d'une section (la Première configuration garde son ancien nom `setup`). */
export function settingsRouteName(slug: SettingsSlug): string {
  if (slug === SETTINGS_HOME) return 'reglages';
  return slug === 'configuration' ? 'setup' : `reglages-${slug}`;
}

/** Adresse d'une section : `/reglages` pour l'accueil, `/reglages/<section>` sinon. */
export function settingsPath(slug: SettingsSlug): string {
  return slug === SETTINGS_HOME ? '/reglages' : `/reglages/${slug}`;
}

/** Section d'une adresse (`/reglages/projets` → `projets`), l'accueil pour `/reglages`, null hors Réglages. */
export function sectionFromPath(path: string): SettingsSlug | null {
  const match = /^\/reglages(?:\/([^/?#]+))?\/?$/.exec(path);
  if (!match) return null;
  if (!match[1]) return SETTINGS_HOME;
  return SETTINGS_SECTIONS.find((s) => s.slug === match[1] && s.slug !== SETTINGS_HOME)?.slug ?? null;
}

export function sectionLabel(slug: SettingsSlug): string {
  return SETTINGS_SECTIONS.find((s) => s.slug === slug)?.label ?? 'Réglages';
}

/** Sections du menu, groupées dans l'ordre (l'accueil seul en tête, sans titre de groupe). */
export function settingsMenu(): { group: string | null; sections: SettingsSection[] }[] {
  const out: { group: string | null; sections: SettingsSection[] }[] = [];
  for (const section of SETTINGS_SECTIONS) {
    const last = out[out.length - 1];
    if (last && last.group === section.group) last.sections.push(section);
    else out.push({ group: section.group, sections: [section] });
  }
  return out;
}

/**
 * Anciennes ancres de la page unique (`/reglages#connexion-axonaut`, liens de la Première configuration et des
 * écrans vides) : section qui contient la carte, ou null.
 */
export function sectionForHash(hash: string): SettingsSlug | null {
  const id = hash.replace(/^#/, '');
  if (!id) return null;
  if (id.startsWith('connexion-')) return 'connexions';
  const anchors: Record<string, SettingsSlug> = {
    imports: 'import',
    membres: 'membres',
    planning: 'projets',
    'modele-etapes': 'projets',
  };
  return anchors[id] ?? null;
}

// ------------------------------------------------------------------ Accueil : une ligne par synchro

export type SyncTone = 'ok' | 'warn' | 'bad';

export interface SyncLine {
  tone: SyncTone;
  /** État en français simple (« il y a 12 min », « en échec : clé refusée »). */
  text: string;
  /** Geste proposé : configurer la connexion, ou la reconnecter (Réglages > Connexions). */
  action: 'configure' | 'reconnect' | null;
}

/** « il y a 12 min », « il y a 3 h », « le 12/10/2026 09:30 ». */
export function sinceText(iso: string, nowMs: number): string {
  const minutes = Math.round((nowMs - Date.parse(iso)) / 60_000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  if (minutes < 24 * 60) return `il y a ${Math.round(minutes / 60)} h`;
  return `le ${new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Paris' })}`;
}

/**
 * État d'une connexion synchronisée (mêmes données que la carte Connexions) : non configurée, en échec, en
 * cours, à jour, ou pas à jour au-delà de `staleAfterHours` sans synchro réussie.
 */
export function connectionSyncLine(
  c: {
    status: 'OK' | 'ERROR' | 'NOT_CONFIGURED';
    lastSyncAt: string | null;
    lastError: string | null;
    syncRunning?: boolean;
    /** Démonstration : la connexion simulée synchronise sans identifiants (jamais « non configurée »). */
    effectiveMode?: 'real' | 'fake';
  },
  nowMs: number,
  staleAfterHours = 2,
): SyncLine {
  if (isUnconfigured(c)) return { tone: 'warn', text: 'non configurée', action: 'configure' };
  if (c.syncRunning) return { tone: 'ok', text: 'synchro en cours…', action: null };
  if (c.status === 'ERROR' || c.lastError)
    return {
      tone: 'bad',
      text: c.lastError ? `en échec : ${c.lastError}` : 'connexion en erreur',
      action: 'reconnect',
    };
  if (!c.lastSyncAt) return { tone: 'warn', text: 'pas encore synchronisée', action: null };
  const ageHours = (nowMs - Date.parse(c.lastSyncAt)) / 3_600_000;
  if (ageHours > staleAfterHours)
    return {
      tone: 'warn',
      text: `pas à jour : dernière synchro ${sinceText(c.lastSyncAt, nowMs)}`,
      action: null,
    };
  return { tone: 'ok', text: sinceText(c.lastSyncAt, nowMs), action: null };
}

/** Connexion sans identifiants hors démonstration (la synchro répond 422 `CONNECTION_NOT_CONFIGURED`). */
export function isUnconfigured(c: { status: string; effectiveMode?: 'real' | 'fake' }): boolean {
  return c.status === 'NOT_CONFIGURED' && c.effectiveMode !== 'fake';
}

/** Agenda personnel d'un membre (lot H) : lu à chaque synchro Google, ou à (re)connecter par la personne. */
export function calendarSyncLine(calendar: 'connected' | 'not_connected' | 'scope_missing'): SyncLine {
  if (calendar === 'connected') return { tone: 'ok', text: 'lu à chaque synchro Google', action: null };
  if (calendar === 'scope_missing')
    return { tone: 'bad', text: 'accès expiré ou agenda non autorisé', action: 'reconnect' };
  return { tone: 'warn', text: 'compte Google pas connecté', action: 'reconnect' };
}
